import React from "react";
import { Link } from "react-router-dom";
import { Container, PageHeader } from "@/components/common";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Heart, Users, Shirt, Footprints, PoundSterling, HandHeart, ArrowRight, PlayCircle, ListChecks } from "lucide-react";

const STEPS = [
  { icon: Users, t: "No partner required", d: "Line dancing is danced in rows — you'll never be left without a partner. Come on your own or bring friends." },
  { icon: Shirt, t: "Wear something comfy", d: "Anything you can move in is perfect. There's no dress code — jeans and a t-shirt are ideal." },
  { icon: Footprints, t: "Comfortable shoes", d: "Flat, smooth-soled shoes work best so you can glide and turn. Trainers are fine to start." },
  { icon: HandHeart, t: "A warm welcome", d: "Just say hello when you arrive — we'll show you where to stand and take it nice and steady." },
];

const FAQS = [
  { q: "I've never danced before. Is that okay?", a: "Absolutely — our Absolute Beginner and Beginner classes are designed for complete newcomers. We break every dance down slowly and repeat it plenty." },
  { q: "Do I need to book?", a: "No booking needed for our weekly classes. Just turn up a few minutes early, pay on the door and join in." },
  { q: "How much does it cost?", a: "Classes are typically £3–£5 each, paid on the night. See the Classes page for exact prices." },
  { q: "What if I get a step wrong?", a: "Everyone does — it's part of the fun! Keep moving, watch the person in front, and you'll pick it up. Nobody's judging." },
  { q: "Can I come and watch first?", a: "Of course. You're welcome to come along, watch a class and have a chat with us before joining in." },
];

export default function NewDancers() {
  return (
    <div>
      <PageHeader eyebrow="New to line dancing?" title="Your first class starts here"
        subtitle="Line dancing is one of the friendliest, most sociable ways to keep moving. Here's everything you need to know before your first visit — no experience needed." />
      <Container className="py-10">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {STEPS.map((s, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBEBE4] text-[#B04B26]"><s.icon size={22} /></div>
              <h3 className="mt-4 font-display text-lg font-bold text-[#2D1E18]">{s.t}</h3>
              <p className="mt-1 leading-relaxed text-[#5C4A42]">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Link to="/basic-steps" data-testid="newdancers-basic-steps" className="card-hover flex items-center gap-4 rounded-2xl border border-border bg-card p-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FBEBE4] text-[#B04B26]"><PlayCircle size={24} /></div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-[#2D1E18]">Learn the basic steps</h3>
              <p className="text-sm text-[#5C4A42]">Short tutorial videos of the moves behind most dances.</p>
            </div>
            <ArrowRight size={18} className="text-[#6E5B51]" />
          </Link>
          <Link to="/dance-floor-etiquette" data-testid="newdancers-etiquette" className="card-hover flex items-center gap-4 rounded-2xl border border-border bg-card p-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FBEBE4] text-[#B04B26]"><ListChecks size={24} /></div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-[#2D1E18]">Dance floor etiquette</h3>
              <p className="text-sm text-[#5C4A42]">A few friendly do's and don'ts for a happy dance floor.</p>
            </div>
            <ArrowRight size={18} className="text-[#6E5B51]" />
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold text-[#2D1E18]">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-4" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-border bg-card mb-3 px-4">
                  <AccordionTrigger className="text-left font-semibold text-[#2D1E18]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#5C4A42]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <aside>
            <div className="rounded-2xl bg-[#2D1E18] p-6 text-white">
              <Heart size={24} className="text-[#E06D44]" />
              <h3 className="mt-3 font-display text-xl font-bold">Ready to give it a go?</h3>
              <p className="mt-2 text-sm text-[#EDE4DA]">Find a beginner class near you and just turn up. We can't wait to meet you.</p>
              <Link to="/classes" data-testid="newdancers-find-class" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C85A32] px-4 py-3 font-bold text-white hover:bg-[#B04B26]">Find a class <ArrowRight size={16} /></Link>
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}
